import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/shared/ui/toast"

const TOAST_LIMIT = 1
// How long a DISMISSED toast stays in state before it's fully removed.
// Needs to be long enough to play the Radix close animation (~300ms) but
// short enough that a stale "open=false" toast can't block a fresh one on
// slow devices. The stock shadcn/ui boilerplate ships 1_000_000ms (≈16 min)
// which is both needless and has bitten us with "toast seems stuck on
// mobile" reports.
const TOAST_REMOVE_DELAY = 5_000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  /**
   * `'high'` marks a toast that EXPLAINS a failure, as opposed to merely
   * reporting one. While such a toast is on screen, an ordinary toast does not
   * replace it — see the ADD_TOAST case below for why that matters.
   *
   * Reach for it only where the alternative message would be actively
   * misleading, not merely less detailed. Today that is the lifecycle 423 in
   * `api/client.ts`, and nothing else.
   */
  priority?: 'high'
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      // Newest-wins plus TOAST_LIMIT = 1 means the SECOND toast of a failure
      // silently deletes the first. On a 423 the api-client toasts the reason
      // ("This studio is read-only — your subscription ended…") and re-rejects;
      // the feature's own onError then fires a hardcoded fallback and wins. The
      // studio owner was left reading "Could not create client." — told the
      // wrong reason for their problem at the one moment they are deciding
      // whether to pay us (p2-subscription F3).
      //
      // The fix lives HERE, in the store, rather than in each module's error
      // handler: there are ~40 write paths and this is the only place all of
      // them pass through. A lock-aware branch per feature would fix the page
      // the finding happened to name and quietly not fix the next one; raising
      // TOAST_LIMIT would show both, and the wrong sentence would still be on
      // screen.
      //
      // The window is bounded by the explanatory toast's own life: `open` goes
      // false when Radix's duration elapses (5s for a destructive toast) or the
      // user dismisses it, and an ordinary toast is free again immediately
      // after. Another high-priority toast is never suppressed, so a second
      // refused write still refreshes the message.
      const blockedByExplanation =
        action.toast.priority !== 'high'
        && state.toasts.some((t) => t.priority === 'high' && t.open)
      if (blockedByExplanation) {
        return state
      }
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }
    }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
